import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Search, Trash2, MapPin, Calendar } from 'lucide-react';
import ConfirmModal from '../../components/ui/ConfirmModal';

export default function AdminLostFound() {
  const [posts,    setPosts]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [type,     setType]     = useState('all');
  const [page,     setPage]     = useState(1);
  const [pages,    setPages]    = useState(1);
  const [total,    setTotal]    = useState(0);

  const [modal, setModal] = useState({
    open: false, postId: null, postTitle: '',
  });

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page });
      if (type !== 'all')  params.set('type', type);
      if (search.trim())   params.set('search', search.trim());
      const { data } = await api.get('/admin/lostfound?' + params.toString());
      setPosts(data.posts);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch {
      toast.error('Could not load posts.');
    } finally {
      setLoading(false);
    }
  }, [type, search, page]);

  useEffect(() => {
    const t = setTimeout(fetchPosts, 300);
    return () => clearTimeout(t);
  }, [fetchPosts]);

  const openModal  = (id, title) => setModal({ open: true, postId: id, postTitle: title });
  const closeModal = ()          => setModal({ open: false, postId: null, postTitle: '' });

  const handleDelete = async () => {
    const id = modal.postId;
    closeModal();
    try {
      await api.delete('/admin/lostfound/' + id);
      setPosts(prev => prev.filter(p => p._id !== id));
      setTotal(t => t - 1);
      toast.success('Post removed and user notified.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove.');
    }
  };

  return (
    <div className="fade-in">

      <ConfirmModal
        isOpen={modal.open}
        onCancel={closeModal}
        onConfirm={handleDelete}
        title="Remove Post?"
        message={'Remove "' + modal.postTitle + '"? The poster will be notified.'}
        confirmText="Remove"
        confirmClass="btn-danger"
      />

      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h2>Lost & Found</h2>
          <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 4 }}>
            {total} post{total !== 1 ? 's' : ''} total
          </p>
        </div>
      </div>

      {/* Controls */}
      <div className="admin-controls">
        <div className="admin-search-wrap">
          <Search size={15} className="admin-search-icon" />
          <input
            className="admin-search"
            placeholder="Search posts..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        <select
          className="select"
          style={{ width: 140 }}
          value={type}
          onChange={e => { setType(e.target.value); setPage(1); }}
        >
          <option value="all">All Types</option>
          <option value="lost">😟 Lost</option>
          <option value="found">🎉 Found</option>
        </select>
      </div>

      {/* Table */}
      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Type</th>
              <th>Title</th>
              <th>Posted By</th>
              <th>Location</th>
              <th>Date Lost/Found</th>
              <th>Posted On</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 40 }}>
                  <div className="spinner" style={{ margin: '0 auto' }} />
                </td>
              </tr>
            ) : posts.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--text-muted)' }}>
                  No posts found
                </td>
              </tr>
            ) : posts.map(post => (
              <tr key={post._id}>

                {/* Type */}
                <td>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', borderRadius: 999,
                    fontSize: '0.75rem', fontWeight: 600,
                    background: post.type === 'lost' ? '#fff7ed' : 'var(--success-soft)',
                    color:      post.type === 'lost' ? '#c2410c' : 'var(--success)',
                    border:     '1px solid ' + (post.type === 'lost' ? '#fed7aa' : '#bbf7d0'),
                  }}>
                    {post.type === 'lost' ? '😟 Lost' : '🎉 Found'}
                  </span>
                </td>

                {/* Title */}
                <td>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', maxWidth: 200 }}>
                    {post.title}
                  </div>
                  <div style={{
                    fontSize: '0.75rem', color: 'var(--text-muted)',
                    maxWidth: 200, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {post.description}
                  </div>
                </td>

                {/* Posted by */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {post.posterSnapshot?.avatar
                      ? <img src={post.posterSnapshot.avatar} alt=""
                          style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      : <div className="avatar-placeholder"
                          style={{ width: 28, height: 28, fontSize: '0.7rem', flexShrink: 0 }}>
                          {post.posterSnapshot?.name?.[0] || '?'}
                        </div>
                    }
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>
                        {post.posterSnapshot?.name || 'Unknown'}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                        {post.posterSnapshot?.rollNumber || '—'}
                      </div>
                    </div>
                  </div>
                </td>

                {/* Location */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.8rem', color: 'var(--text-muted)', maxWidth: 160 }}>
                    <MapPin size={12} style={{ flexShrink: 0 }} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {post.location}
                    </span>
                  </div>
                </td>

                {/* Date lost/found */}
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    <Calendar size={12} />
                    {new Date(post.date).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'short', year: 'numeric',
                    })}
                  </div>
                </td>

                {/* Posted on */}
                <td style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                  {new Date(post.createdAt).toLocaleDateString('en-IN', {
                    day: 'numeric', month: 'short', year: 'numeric',
                  })}
                </td>

                {/* Status */}
                <td>
                  <span className={'badge ' + (post.status === 'open' ? 'badge-success' : 'badge-neutral')}>
                    {post.status === 'open' ? 'Open' : 'Resolved'}
                  </span>
                </td>

                {/* Action */}
                <td>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => openModal(post._id, post.title)}
                    title="Remove post"
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </td>

              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 24 }}>
          <button className="btn btn-secondary btn-sm"
            disabled={page === 1} onClick={() => setPage(p => p - 1)}>
            ← Prev
          </button>
          <span style={{ lineHeight: '36px', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {page} / {pages}
          </span>
          <button className="btn btn-secondary btn-sm"
            disabled={page === pages} onClick={() => setPage(p => p + 1)}>
            Next →
          </button>
        </div>
      )}
    </div>
  );
}